import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { Card, Button, Badge, Empty, Spinner, fmtBRL, Input } from "@/components/ui/Primitives";
import { api, formatErr } from "@/lib/api";
import { FileCheck2, ShieldAlert, PlugZap, Settings as SettingsIcon, RefreshCw, Download, X, XCircle, CheckCircle2, Clock, AlertTriangle, Scissors, Plus, Trash2 } from "lucide-react";

const STATUS_META = {
  autorizado: { label: "Autorizada", tone: "success", icon: CheckCircle2 },
  processando: { label: "Processando", tone: "warn", icon: Clock },
  erro_autorizacao: { label: "Rejeitada", tone: "danger", icon: XCircle },
  erro_envio: { label: "Erro envio", tone: "danger", icon: XCircle },
  cancelado: { label: "Cancelada", tone: "neutral", icon: XCircle },
  denegado: { label: "Denegada", tone: "danger", icon: XCircle },
  cancelamento_pendente: { label: "Cancel. pendente", tone: "warn", icon: Clock },
};

export default function Faturamento() {
  const [items, setItems] = useState([]);
  const [kpis, setKpis] = useState({ pendentes: 0, autorizadas: 0, rejeitadas: 0, canceladas: 0 });
  const [loading, setLoading] = useState(true);
  const [focusStatus, setFocusStatus] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [cancelModal, setCancelModal] = useState(null);
  const [justificativa, setJustificativa] = useState("");

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const [list, focus] = await Promise.all([
        api.get("/faturamento/list"),
        api.get("/faturamento/focus/status"),
      ]);
      setItems(list.data.items || []);
      setKpis(list.data.kpis || {});
      setFocusStatus(focus.data);
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Auto-refresh: se houver alguma NF-e com status 'processando', consulta a Focus
  // a cada 8s até o status mudar (autorizado/erro_envio/erro_autorizacao/…).
  useEffect(() => {
    const pendentes = [];
    for (const s of items) {
      if (s.nfe_splits && s.nfe_splits.length) {
        for (const sp of s.nfe_splits) {
          if ((sp.nfe_status || "").toLowerCase() === "processando" && sp.referencia) {
            pendentes.push({ ship: s.id, split: sp.index });
          }
        }
      } else if (s.nfe_referencia && s.nfe_status === "processando") {
        pendentes.push({ ship: s.id, split: null });
      }
    }
    if (pendentes.length === 0) return undefined;
    let cancelled = false;
    const tick = async () => {
      for (const p of pendentes) {
        if (cancelled) return;
        try {
          const qs = p.split ? `?split=${p.split}` : "";
          await api.get(`/faturamento/consultar/${p.ship}${qs}`);
        } catch (_) { /* silencioso — segue tentando */ }
      }
      if (!cancelled) await load();
    };
    const id = setInterval(tick, 8000);
    return () => { cancelled = true; clearInterval(id); };
  }, [items]);

  const emitir = async (id) => {
    setBusy((b) => ({ ...b, [id]: "emitir" }));
    setErr("");
    try {
      // 1) Validação prévia — pega problemas comuns antes de mandar para SEFAZ
      const val = await api.get(`/faturamento/validar/${id}`);
      if (!val.data.ok) {
        const lista = (val.data.problemas || []).map((p, i) => `  ${i+1}. ${p}`).join("\n");
        alert(`⚠️ Corrija antes de emitir a NF-e:\n\n${lista}`);
        setBusy((b) => ({ ...b, [id]: null }));
        return;
      }
      if (!window.confirm(`Emitir NF-e para esta saída em ${focusStatus?.env}?\n\n⚠️ Certifique-se de que o certificado A1 está subido no painel Focus.`)) {
        setBusy((b) => ({ ...b, [id]: null }));
        return;
      }
      const { data } = await api.post(`/faturamento/emitir/${id}`);
      if (data.ok) {
        alert(`✔ NF-e enviada!\nReferência: ${data.referencia}\nStatus: ${data.status || "processando"}`);
      } else {
        const errList = (data.erro?.erros || []).map((e) => `• ${e.mensagem}`).join("\n");
        const errBase = data.erro?.mensagem || JSON.stringify(data.erro || data);
        alert(`❌ Falha ao enviar:\n${errBase}\n\n${errList}`);
      }
      await load();
    } catch (e) { alert(formatErr(e)); }
    finally { setBusy((b) => ({ ...b, [id]: null })); }
  };

  // key = `${shipmentId}` para NF única e `${shipmentId}#${splitIndex}` para split
  const reconsultar = async (shipId, split) => {
    const key = split ? `${shipId}#${split}` : shipId;
    setBusy((b) => ({ ...b, [key]: "consulta" }));
    try {
      const qs = split ? `?split=${split}` : "";
      await api.get(`/faturamento/consultar/${shipId}${qs}`);
      await load();
    } catch (e) { alert(formatErr(e)); }
    finally { setBusy((b) => ({ ...b, [key]: null })); }
  };

  const reemitir = async (id) => {
    if (!window.confirm("Reemitir esta NF-e?\n\nO histórico da tentativa anterior será apagado (referência, status, resposta da SEFAZ) e uma nova emissão será iniciada.")) return;
    setBusy((b) => ({ ...b, [id]: "reemitir" }));
    try {
      await api.post(`/faturamento/limpar/${id}`);
      await emitir(id);
    } catch (e) { alert(formatErr(e)); setBusy((b) => ({ ...b, [id]: null })); }
  };

  // -------- Divisão em múltiplas NF-e --------
  const [splitOpen, setSplitOpen] = useState(false);
  const [splitShip, setSplitShip] = useState(null);
  const [splitQtys, setSplitQtys] = useState(["", ""]);
  const [splitBusy, setSplitBusy] = useState(false);
  const [splitErr, setSplitErr] = useState("");

  const openSplit = (ship) => {
    setSplitShip(ship);
    const total = Number(ship?.total_caixas || 0);
    // Sugere 75/25 como default (exemplo comum)
    setSplitQtys([Math.round(total * 0.75).toString(), Math.round(total * 0.25).toString()]);
    setSplitErr("");
    setSplitOpen(true);
  };
  const setSplitQty = (idx, v) => setSplitQtys((s) => s.map((x, i) => i === idx ? v : x));
  const addSplit = () => setSplitQtys((s) => [...s, ""]);
  const removeSplit = (idx) => setSplitQtys((s) => s.length <= 2 ? s : s.filter((_, i) => i !== idx));
  const confirmSplit = async () => {
    const total = Number(splitShip?.total_caixas || 0);
    const nums = splitQtys.map((x) => Number(x) || 0);
    const soma = nums.reduce((a, b) => a + b, 0);
    if (nums.some((n) => n <= 0)) { setSplitErr("Cada NF precisa ter quantidade > 0."); return; }
    if (soma > total + 0.001) { setSplitErr(`Soma (${soma}) excede o total da carga (${total}).`); return; }
    if (!window.confirm(`Confirmar emissão de ${nums.length} NF-e a partir desta Saída?\n\n${nums.map((n, i) => `NF ${i+1}: ${n} sacos`).join("\n")}\n\nTotal: ${soma} de ${total} sacos`)) return;
    setSplitBusy(true); setSplitErr("");
    try {
      const { data } = await api.post(`/faturamento/emitir-multi/${splitShip.id}`, { quantidades: nums });
      const summary = (data.splits || []).map((s, i) => `NF ${i+1} (${s.quantidade} sc): ${s.nfe_status}`).join("\n");
      alert(`✔ Divisão concluída:\n\n${summary}`);
      setSplitOpen(false);
      await load();
    } catch (e) { setSplitErr(formatErr(e)); }
    finally { setSplitBusy(false); }
  };

  const baixarPDF = async (id, split) => {
    try {
      const qs = split ? `?split=${split}` : "";
      const res = await api.get(`/faturamento/${id}/pdf${qs}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = split ? `danfe_${id}_nf${split}.pdf` : `danfe_${id}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { alert(formatErr(e)); }
  };

  const baixarXML = async (id, split) => {
    try {
      const qs = split ? `?split=${split}` : "";
      const res = await api.get(`/faturamento/${id}/xml${qs}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/xml" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = split ? `nfe_${id}_nf${split}.xml` : `nfe_${id}.xml`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { alert(formatErr(e)); }
  };

  // cancelModal pode ser string (shipmentId) ou objeto { id, split } quando for split
  const openCancelar = (id, split) => {
    setCancelModal(split ? { id, split } : id);
    setJustificativa("");
  };
  const cancelarNfe = async () => {
    if (justificativa.trim().length < 15) { alert("Justificativa precisa ter no mínimo 15 caracteres."); return; }
    const target = typeof cancelModal === "object" && cancelModal ? cancelModal : { id: cancelModal, split: null };
    const busyKey = target.split ? `${target.id}#${target.split}` : target.id;
    setBusy((b) => ({ ...b, [busyKey]: "cancelar" }));
    try {
      const qs = target.split ? `?split=${target.split}` : "";
      const { data } = await api.post(`/faturamento/${target.id}/cancelar${qs}`, { justificativa });
      alert(data.ok ? "Cancelamento enviado à SEFAZ" : `Falha: ${JSON.stringify(data.data)}`);
      setCancelModal(null);
      await load();
    } catch (e) { alert(formatErr(e)); }
    finally { setBusy((b) => ({ ...b, [busyKey]: null })); }
  };

  const focusOK = focusStatus?.ok && focusStatus?.configured;

  return (
    <Layout
      title="Faturamento — NF-e"
      subtitle={`Emissão via Focus NF-e — ambiente ${focusStatus?.env || "…"}`}
      actions={
        <div className="flex gap-2">
          <Button variant="secondary" onClick={load} data-testid="fat-refresh">
            <RefreshCw size={14} /> Atualizar
          </Button>
        </div>
      }
    >
      {err && <div className="text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md mb-4">{err}</div>}

      {/* Banner de status Focus */}
      <div
        className={`mb-6 p-4 rounded-xl flex items-start gap-3 ${focusOK ? "bg-emerald-50 border border-emerald-300" : "bg-amber-50 border border-amber-300"}`}
        data-testid="focus-status-banner"
      >
        {focusOK ? <CheckCircle2 className="text-emerald-600 shrink-0 mt-0.5" size={20} /> : <ShieldAlert className="text-warn shrink-0 mt-0.5" size={20} />}
        <div className="flex-1">
          <div className={`font-heading font-bold ${focusOK ? "text-emerald-900" : "text-amber-900"}`}>
            {focusOK ? "Focus NF-e conectada" : "Focus NF-e — verificar configuração"}
          </div>
          <p className={`text-sm mt-1 ${focusOK ? "text-emerald-900/80" : "text-amber-900/80"}`}>
            Ambiente: <strong>{focusStatus?.env}</strong> · URL: <code className="text-xs">{focusStatus?.base_url}</code>
            {focusOK
              ? " · Token validado. Pronto para emitir. Confira se o certificado A1 já foi carregado no painel."
              : ` · ${focusStatus?.error || focusStatus?.message || "Verifique o token."}`}
          </p>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Kpi label="Pendentes" value={kpis.pendentes} tone="warn" />
        <Kpi label="Autorizadas" value={kpis.autorizadas} tone="ok" />
        <Kpi label="Rejeitadas" value={kpis.rejeitadas} tone="danger" />
        <Kpi label="Canceladas" value={kpis.canceladas} tone="neutral" />
      </div>

      {/* Tabela */}
      <Card className="overflow-hidden">
        <div className="px-6 py-4 border-b border-ink-200 flex items-center gap-2">
          <FileCheck2 size={18} className="text-brand-500" />
          <h3 className="font-heading text-lg font-bold text-ink-900">Saídas finalizadas</h3>
        </div>
        {loading ? (
          <div className="flex justify-center py-16"><Spinner size={26} /></div>
        ) : items.length === 0 ? (
          <Empty title="Nenhuma saída finalizada" hint="Finalize uma saída em /shipments para emitir a NF-e." />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-stone-50 border-b border-ink-200">
              <tr className="text-left text-xs font-bold uppercase tracking-wider text-ink-500">
                <th className="px-4 py-3">RS</th>
                <th className="px-4 py-3">Cliente</th>
                <th className="px-4 py-3">Destino</th>
                <th className="px-4 py-3 text-right">Valor</th>
                <th className="px-4 py-3">Status NF-e</th>
                <th className="px-4 py-3">Referência</th>
                <th className="px-4 py-3 w-1">Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.flatMap((s) => {
                const hasSplits = Array.isArray(s.nfe_splits) && s.nfe_splits.length > 0;
                const rows = [];

                if (hasSplits) {
                  // ---- LINHA PAI: apenas resumo, sem ações de emissão ----
                  const okCount = s.nfe_splits.filter((sp) => sp.nfe_status === "autorizado").length;
                  rows.push(
                    <tr key={s.id} className="border-b border-ink-200 bg-brand-50/40">
                      <td className="px-4 py-3 font-mono">#{s.numero_rs || s.id.slice(0, 8)}</td>
                      <td className="px-4 py-3 font-medium">{s.cliente_nome || "—"}</td>
                      <td className="px-4 py-3 text-ink-700">{s.destino || "—"}</td>
                      <td className="px-4 py-3 font-mono text-right">{fmtBRL(s.valor_total || s.valor_bruto || 0)}</td>
                      <td className="px-4 py-3">
                        <Badge tone="info">
                          <Scissors size={12} className="inline mr-1" />
                          Dividida em {s.nfe_splits.length} NF-e ({okCount}/{s.nfe_splits.length} OK)
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-[11px] text-ink-500">—</td>
                      <td className="px-4 py-3 text-[11px] text-ink-500 italic">Detalhes abaixo</td>
                    </tr>
                  );
                  // ---- LINHAS FILHAS: uma por NF-e emitida ----
                  for (const sp of s.nfe_splits) {
                    const meta = STATUS_META[sp.nfe_status] || null;
                    const key = `${s.id}#${sp.index}`;
                    const isBusy = busy[key];
                    const ratio = Number(sp.ratio || (Number(sp.quantidade || 0) / Math.max(1, Number(s.total_caixas || 1))));
                    const valorSplit = (Number(s.valor_total || s.valor_bruto || 0)) * ratio;
                    const chave = sp.nfe_data?.chave_nfe;
                    const numero = sp.nfe_data?.numero;
                    rows.push(
                      <tr key={key} className="border-b border-ink-200 hover:bg-stone-50 transition-colors text-[13px]">
                        <td className="px-4 py-2.5 pl-10 font-mono text-ink-500">↳ NF {sp.index}/{s.nfe_splits.length}</td>
                        <td className="px-4 py-2.5 text-ink-600">
                          <span className="text-xs">{Number(sp.quantidade || 0)} sc {numero ? `· nº ${numero}` : ""}</span>
                        </td>
                        <td className="px-4 py-2.5 text-ink-600 text-xs font-mono">{chave ? `${chave.slice(0, 8)}…${chave.slice(-4)}` : "—"}</td>
                        <td className="px-4 py-2.5 font-mono text-right text-ink-700">{fmtBRL(valorSplit)}</td>
                        <td className="px-4 py-2.5">
                          {meta ? (
                            <Badge tone={meta.tone}><meta.icon size={12} className="inline mr-1" />{meta.label}</Badge>
                          ) : (
                            <Badge tone="neutral">— {sp.nfe_status || "?"} —</Badge>
                          )}
                        </td>
                        <td className="px-4 py-2.5 font-mono text-[10px] text-ink-500">{sp.referencia || "—"}</td>
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-1">
                            {sp.nfe_status && sp.nfe_status !== "autorizado" && sp.nfe_status !== "cancelado" && (
                              <button
                                onClick={() => reconsultar(s.id, sp.index)}
                                className="p-1.5 rounded-md hover:bg-blue-100 text-blue-700"
                                title="Consultar SEFAZ"
                                disabled={isBusy}
                                data-testid={`reconsultar-${s.id}-${sp.index}`}
                              >
                                {isBusy === "consulta" ? <Spinner size={12} /> : <RefreshCw size={14} />}
                              </button>
                            )}
                            {sp.nfe_status === "autorizado" && (
                              <>
                                <button onClick={() => baixarPDF(s.id, sp.index)} className="p-1.5 rounded-md hover:bg-emerald-100 text-emerald-700" title="Baixar DANFE PDF" data-testid={`pdf-${s.id}-${sp.index}`}>
                                  <Download size={14} />
                                </button>
                                <button onClick={() => baixarXML(s.id, sp.index)} className="p-1.5 rounded-md hover:bg-emerald-100 text-emerald-700" title="Baixar XML autorizado" data-testid={`xml-${s.id}-${sp.index}`}>
                                  <FileCheck2 size={14} />
                                </button>
                                <button onClick={() => openCancelar(s.id, sp.index)} className="p-1.5 rounded-md hover:bg-red-100 text-danger" title="Cancelar NF-e (24h)" data-testid={`cancelar-${s.id}-${sp.index}`}>
                                  <XCircle size={14} />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  }
                  return rows;
                }

                // ---- SHIPMENT SEM SPLITS: renderização clássica ----
                const meta = STATUS_META[s.nfe_status] || null;
                const emitted = !!s.nfe_referencia;
                const isBusy = busy[s.id];
                rows.push(
                  <tr key={s.id} className="border-b border-ink-200 hover:bg-stone-50 transition-colors">
                    <td className="px-4 py-3 font-mono">#{s.numero_rs || s.id.slice(0, 8)}</td>
                    <td className="px-4 py-3">{s.cliente_nome || "—"}</td>
                    <td className="px-4 py-3 text-ink-700">{s.destino || "—"}</td>
                    <td className="px-4 py-3 font-mono text-right">{fmtBRL(s.valor_total || s.valor_bruto || 0)}</td>
                    <td className="px-4 py-3">
                      {meta ? (
                        <Badge tone={meta.tone}><meta.icon size={12} className="inline mr-1" />{meta.label}</Badge>
                      ) : (
                        <Badge tone="neutral">— não emitida —</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-[11px] text-ink-500">{s.nfe_referencia || "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        {!emitted && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => emitir(s.id)}
                              disabled={!focusOK || isBusy}
                              data-testid={`emitir-${s.id}`}
                            >
                              {isBusy === "emitir" ? <Spinner size={12} /> : <PlugZap size={14} />} Emitir
                            </Button>
                            <button
                              onClick={() => openSplit(s)}
                              className="p-1.5 rounded-md hover:bg-brand-100 text-brand-700"
                              title="Dividir em várias NF-e"
                              disabled={!focusOK || isBusy}
                              data-testid={`dividir-${s.id}`}
                            >
                              <Scissors size={14} />
                            </button>
                          </>
                        )}
                        {emitted && s.nfe_status !== "autorizado" && s.nfe_status !== "cancelado" && (
                          <button
                            onClick={() => reconsultar(s.id)}
                            className="p-1.5 rounded-md hover:bg-blue-100 text-blue-700"
                            title="Consultar SEFAZ"
                            disabled={isBusy}
                            data-testid={`reconsultar-${s.id}`}
                          >
                            {isBusy === "consulta" ? <Spinner size={12} /> : <RefreshCw size={14} />}
                          </button>
                        )}
                        {emitted && ["erro_envio", "erro_autorizacao", "denegado"].includes(s.nfe_status) && (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => reemitir(s.id)}
                            disabled={!focusOK || isBusy}
                            data-testid={`reemitir-${s.id}`}
                            title="Limpa o histórico e tenta emitir de novo"
                          >
                            {isBusy === "reemitir" || isBusy === "emitir" ? <Spinner size={12} /> : <PlugZap size={14} />} Reemitir
                          </Button>
                        )}
                        {s.nfe_status === "autorizado" && (
                          <>
                            <button onClick={() => baixarPDF(s.id)} className="p-1.5 rounded-md hover:bg-emerald-100 text-emerald-700" title="Baixar DANFE PDF" data-testid={`pdf-${s.id}`}>
                              <Download size={14} />
                            </button>
                            <button onClick={() => baixarXML(s.id)} className="p-1.5 rounded-md hover:bg-emerald-100 text-emerald-700" title="Baixar XML autorizado" data-testid={`xml-${s.id}`}>
                              <FileCheck2 size={14} />
                            </button>
                            <button onClick={() => openCancelar(s.id)} className="p-1.5 rounded-md hover:bg-red-100 text-danger" title="Cancelar NF-e (24h)" data-testid={`cancelar-${s.id}`}>
                              <XCircle size={14} />
                            </button>
                          </>
                        )}
                        {emitted && (
                          <button
                            onClick={() => setExpanded(expanded === s.id ? null : s.id)}
                            className="p-1.5 rounded-md hover:bg-stone-100 text-ink-500"
                            title="Detalhes"
                            data-testid={`detail-${s.id}`}
                          >
                            <AlertTriangle size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
                if (expanded === s.id && s.nfe_data) {
                  rows.push(
                    <tr key={`${s.id}-det`} className="bg-stone-50 border-b border-ink-200">
                      <td colSpan={7} className="px-8 py-4">
                        <div className="text-xs">
                          <div className="font-bold uppercase text-ink-500 mb-2">Retorno da SEFAZ</div>
                          {s.nfe_data.chave_nfe && <div><strong>Chave:</strong> <span className="font-mono">{s.nfe_data.chave_nfe}</span></div>}
                          {s.nfe_data.numero && <div><strong>Número:</strong> {s.nfe_data.numero} · Série: {s.nfe_data.serie}</div>}
                          {s.nfe_data.mensagem_sefaz && <div className="mt-1"><strong>Mensagem:</strong> {s.nfe_data.mensagem_sefaz}</div>}
                          {s.nfe_data.erros && (
                            <div className="mt-2 text-danger">
                              <strong>Erros:</strong>
                              <ul className="list-disc list-inside">
                                {(Array.isArray(s.nfe_data.erros) ? s.nfe_data.erros : [s.nfe_data.erros]).map((e, i) => (
                                  <li key={i}>{typeof e === "string" ? e : (e.mensagem || JSON.stringify(e))}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          <pre className="mt-3 p-2 bg-white border border-ink-200 rounded text-[10px] overflow-x-auto max-h-40">{JSON.stringify(s.nfe_data, null, 2)}</pre>
                        </div>
                      </td>
                    </tr>
                  );
                }
                return rows;
              })}
            </tbody>
          </table>
        )}
      </Card>

      {/* Modal cancelamento */}
      {cancelModal && (
        <div className="fixed inset-0 z-50 bg-stone-900/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b border-ink-200">
              <h3 className="font-heading font-bold text-ink-900">
                Cancelar NF-e
                {typeof cancelModal === "object" && cancelModal.split ? ` (Split ${cancelModal.split})` : ""}
              </h3>
              <button onClick={() => setCancelModal(null)} className="text-ink-500"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 px-3 py-2 rounded-md">
                ⚠️ O cancelamento só é permitido até <strong>24 horas</strong> após a autorização da NF-e.
                A mercadoria não pode ter transitado.
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-ink-500 mb-1">Justificativa (mín. 15 caracteres)</label>
                <textarea
                  value={justificativa}
                  onChange={(e) => setJustificativa(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-ink-200 rounded-md text-sm"
                  placeholder="Ex.: Erro na quantidade de mercadoria emitida"
                  data-testid="cancel-justificativa"
                />
                <div className="text-[10px] text-ink-500 mt-1">{justificativa.length}/15 caracteres mínimos</div>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-ink-200">
              <Button variant="secondary" onClick={() => setCancelModal(null)}>Voltar</Button>
              <Button onClick={cancelarNfe} disabled={justificativa.trim().length < 15} data-testid="cancel-submit">Confirmar cancelamento</Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal — Dividir NF-e */}
      {splitOpen && splitShip && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => !splitBusy && setSplitOpen(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-ink-200">
              <div className="flex items-center gap-2">
                <Scissors size={18} className="text-brand-600" />
                <h3 className="font-heading text-lg font-bold text-ink-900">Dividir em múltiplas NF-e</h3>
              </div>
              <button onClick={() => setSplitOpen(false)} disabled={splitBusy}><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-ink-700">
                Carga total: <strong className="font-mono">{Number(splitShip.total_caixas || 0)} sacos</strong>. Cada NF terá quantidade proporcional; itens e valores são divididos automaticamente.
              </p>
              {splitQtys.map((q, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <div className="w-14 text-xs font-semibold text-ink-500 uppercase">NF {idx + 1}</div>
                  <input
                    type="number"
                    min={1}
                    value={q}
                    onChange={(e) => setSplitQty(idx, e.target.value)}
                    className="flex-1 rounded-md bg-stone-50 border border-stone-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500"
                    placeholder="Quantidade (sacos)"
                    disabled={splitBusy}
                    data-testid={`split-qty-${idx}`}
                  />
                  {splitQtys.length > 2 && (
                    <button type="button" onClick={() => removeSplit(idx)} className="p-2 rounded-md hover:bg-red-100 text-danger" disabled={splitBusy}>
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
              <div className="flex justify-between items-center pt-1">
                <button type="button" onClick={addSplit} className="text-xs text-brand-600 font-semibold hover:underline flex items-center gap-1" disabled={splitBusy}>
                  <Plus size={12} /> Adicionar mais uma NF
                </button>
                <div className="text-xs">
                  Soma: <span className="font-mono font-bold">{splitQtys.reduce((a, b) => a + (Number(b) || 0), 0)}</span>/{Number(splitShip.total_caixas || 0)}
                </div>
              </div>
              {splitErr && <div className="text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md">{splitErr}</div>}
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-ink-200">
              <Button variant="secondary" onClick={() => setSplitOpen(false)} disabled={splitBusy}>Voltar</Button>
              <Button onClick={confirmSplit} disabled={splitBusy} data-testid="split-confirm">
                {splitBusy ? <Spinner size={14} /> : <><Scissors size={14} /> Emitir {splitQtys.length} NF-e</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

function Kpi({ label, value, tone = "default" }) {
  const tones = { default: "text-ink-900", ok: "text-ok", danger: "text-danger", warn: "text-warn", neutral: "text-ink-700" };
  return (
    <div className="p-4 rounded-lg border bg-white border-ink-200">
      <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-500">{label}</div>
      <div className={`font-mono text-2xl font-semibold mt-1 ${tones[tone]}`}>{value}</div>
    </div>
  );
}
